import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Hidden,
  MenuList,
  MenuItem,
  Paper,
  Popper
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const REPORT_SUBMENU_CLOSE_DELAY = 350;
const REPORT_REOPEN_GUARD_DELAY = 180;
const MENU_TRANSITION_SETTLE_DELAY = 160;

const TopBarDesktopNavigation = ({
  classes,
  listMenu,
  canShowDropdownMenus,
  canShowReportMenu,
  canShowDeviceLookupMenu,
  isPathActive,
  isManageActive,
  isReportActive,
  isDeviceLookupActive,
  switchRoute,
  manageMenuAnchor,
  reportMenuAnchor,
  onManageMouseEnter,
  onManageMouseLeave,
  onManageMenuMouseEnter,
  onManageMenuMouseLeave,
  onReportMouseEnter,
  onReportMouseLeave,
  onReportMenuMouseEnter,
  onReportMenuMouseLeave,
  closeManageMenu,
  closeReportMenu,
  manageMenuItems,
  reportMenuItems,
  handleDesktopMenuItemClick
}) => {
  const [reportSubmenuAnchor, setReportSubmenuAnchor] = useState(null);
  const [activeReportSubmenuItems, setActiveReportSubmenuItems] = useState([]);
  const [reportNestedSubmenuAnchor, setReportNestedSubmenuAnchor] = useState(null);
  const [activeReportNestedSubmenuItems, setActiveReportNestedSubmenuItems] = useState([]);
  const [reportMenuStyle, setReportMenuStyle] = useState(null);
  const [reportSubmenuStyle, setReportSubmenuStyle] = useState(null);
  const [reportNestedSubmenuStyle, setReportNestedSubmenuStyle] = useState(null);
  const activeReportParentKeyRef = useRef(null);
  const activeReportNestedParentKeyRef = useRef(null);
  const reportSubmenuCloseTimeoutRef = useRef(null);
  const reportInteractionLockedUntilRef = useRef(0);
  const reportMenuOpenedAtRef = useRef(0);
  const deferredSubmenuTimeoutRef = useRef(null);
  const reportMenuPaperRef = useRef(null);
  const reportSubmenuPaperRef = useRef(null);
  const isReportMenuOpen = Boolean(reportMenuAnchor);

  useEffect(() => {
    if (isReportMenuOpen) {
      reportMenuOpenedAtRef.current = Date.now();
      const reportButtonRect = reportMenuAnchor?.getBoundingClientRect?.();
      if (reportButtonRect) {
        setReportMenuStyle({
          position: 'fixed',
          top: Math.round(reportButtonRect.bottom - 4),
          left: Math.round(reportButtonRect.left),
          zIndex: 1300
        });
      }
    } else {
      clearReportSubmenuCloseTimeout();
      clearDeferredSubmenuTimeout();
      setReportMenuStyle(null);
      setReportSubmenuAnchor(null);
      setActiveReportSubmenuItems([]);
      setReportNestedSubmenuAnchor(null);
      setActiveReportNestedSubmenuItems([]);
      setReportSubmenuStyle(null);
      setReportNestedSubmenuStyle(null);
      activeReportParentKeyRef.current = null;
      activeReportNestedParentKeyRef.current = null;
    }
  }, [isReportMenuOpen]);

  const clearReportSubmenuCloseTimeout = () => {
    if (reportSubmenuCloseTimeoutRef.current) {
      clearTimeout(reportSubmenuCloseTimeoutRef.current);
      reportSubmenuCloseTimeoutRef.current = null;
    }
  };

  const clearDeferredSubmenuTimeout = () => {
    if (deferredSubmenuTimeoutRef.current) {
      clearTimeout(deferredSubmenuTimeoutRef.current);
      deferredSubmenuTimeoutRef.current = null;
    }
  };

  const closeReportSubmenu = () => {
    clearReportSubmenuCloseTimeout();
    clearDeferredSubmenuTimeout();
    setReportSubmenuAnchor(null);
    setActiveReportSubmenuItems([]);
    setReportNestedSubmenuAnchor(null);
    setActiveReportNestedSubmenuItems([]);
    setReportSubmenuStyle(null);
    setReportNestedSubmenuStyle(null);
    activeReportParentKeyRef.current = null;
    activeReportNestedParentKeyRef.current = null;
  };

  const scheduleReportSubmenuClose = () => {
    clearReportSubmenuCloseTimeout();
    reportSubmenuCloseTimeoutRef.current = setTimeout(() => {
      setReportSubmenuAnchor(null);
      setActiveReportSubmenuItems([]);
      setReportNestedSubmenuAnchor(null);
      setActiveReportNestedSubmenuItems([]);
      setReportSubmenuStyle(null);
      setReportNestedSubmenuStyle(null);
      activeReportParentKeyRef.current = null;
      activeReportNestedParentKeyRef.current = null;
      reportSubmenuCloseTimeoutRef.current = null;
    }, REPORT_SUBMENU_CLOSE_DELAY);
  };

  const renderMainMenuClass = isActive =>
    clsx('itemMenu', { 'itemMenu-active': isActive });

  const getDesktopDropdownButtonClass = isActive =>
    clsx(classes.desktopMenuButton, {
      [classes.desktopMenuButtonActive]: isActive
    });

  const getDesktopDropdownIconClass = isOpen =>
    clsx(classes.desktopMenuIcon, {
      [classes.desktopMenuIconOpen]: isOpen
    });

  const handleReportItemMouseEnter = (event, item, index) => {
    if (Date.now() < reportInteractionLockedUntilRef.current) {
      closeReportSubmenu();
      return;
    }

    if (!item.children?.length) {
      closeReportSubmenu();
      return;
    }

    const parentKey = `${item.route}-${index}`;
    clearReportSubmenuCloseTimeout();

    if (
      activeReportParentKeyRef.current === parentKey &&
      reportSubmenuAnchor &&
      activeReportSubmenuItems === item.children
    ) {
      return;
    }

    const targetElement = event.currentTarget;
    const timeSinceMenuOpen = Date.now() - reportMenuOpenedAtRef.current;

    const openSubmenu = () => {
      const anchorRect = targetElement.getBoundingClientRect();
      const menuRect = reportMenuPaperRef.current?.getBoundingClientRect();

      activeReportParentKeyRef.current = parentKey;
      setReportSubmenuAnchor(targetElement);
      setActiveReportSubmenuItems(item.children);
      setReportSubmenuStyle(
        menuRect
          ? {
              position: 'fixed',
              top: Math.round(anchorRect.top),
              left: Math.round(menuRect.right),
              zIndex: 1600
            }
          : null
      );
      setReportNestedSubmenuAnchor(null);
      setActiveReportNestedSubmenuItems([]);
      setReportNestedSubmenuStyle(null);
      activeReportNestedParentKeyRef.current = null;
    };

    clearDeferredSubmenuTimeout();

    if (timeSinceMenuOpen < MENU_TRANSITION_SETTLE_DELAY) {
      deferredSubmenuTimeoutRef.current = setTimeout(() => {
        openSubmenu();
        deferredSubmenuTimeoutRef.current = null;
      }, MENU_TRANSITION_SETTLE_DELAY - timeSinceMenuOpen);
    } else {
      openSubmenu();
    }
  };

  const handleReportLeafItemClick = route => {
    reportInteractionLockedUntilRef.current =
      Date.now() + REPORT_REOPEN_GUARD_DELAY;
    closeReportSubmenu();
    handleDesktopMenuItemClick(route);
  };

  const handleReportSubmenuItemMouseEnter = (event, item, index) => {
    if (!item.children?.length) {
      setReportNestedSubmenuAnchor(null);
      setActiveReportNestedSubmenuItems([]);
      setReportNestedSubmenuStyle(null);
      activeReportNestedParentKeyRef.current = null;
      return;
    }

    const parentKey = `${item.route || item.label}-${index}`;

    if (
      activeReportNestedParentKeyRef.current === parentKey &&
      reportNestedSubmenuAnchor &&
      activeReportNestedSubmenuItems === item.children
    ) {
      return;
    }

    const anchorRect = event.currentTarget.getBoundingClientRect();
    const submenuRect = reportSubmenuPaperRef.current?.getBoundingClientRect();

    activeReportNestedParentKeyRef.current = parentKey;
    setReportNestedSubmenuAnchor(event.currentTarget);
    setActiveReportNestedSubmenuItems(item.children);
    setReportNestedSubmenuStyle(
      submenuRect
        ? {
            position: 'fixed',
            top: Math.round(anchorRect.top),
            left: Math.round(submenuRect.right),
            zIndex: 1700
          }
        : null
    );
  };

  const handleReportSubmenuLeafItemClick = route => {
    closeReportSubmenu();
    closeReportMenu();
    handleDesktopMenuItemClick(route);
  };

  const renderManageMenuItems = items =>
    items.map((item, index) => (
      <React.Fragment key={`${item.route}-${index}`}>
        <MenuItem
          onClick={() => handleDesktopMenuItemClick(item.route)}
          className={classes.itemDropdownMenu}
        >
          {item.label}
        </MenuItem>
        {index < items.length - 1 && <Divider className={classes.dropdownDivider} />}
      </React.Fragment>
    ));

  const renderReportMenuItems = items =>
    items.map((item, index) => (
      <React.Fragment key={`${item.route}-${index}`}>
        <MenuItem
          onClick={() => !item.children?.length && handleReportLeafItemClick(item.route)}
          className={classes.itemDropdownMenu}
          onMouseEnter={event => handleReportItemMouseEnter(event, item, index)}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <span>{item.label}</span>
            {item.children?.length ? <ChevronRightIcon fontSize="small" /> : null}
          </Box>
        </MenuItem>
        {index < items.length - 1 && <Divider className={classes.dropdownDivider} />}
      </React.Fragment>
    ));

  return (
    <Hidden mdDown>
      <Box flexGrow={1} className={classes.menuHeader}>
        <ul className={classes.listItem}>
          {listMenu.map(menu => (
            <li
              className={renderMainMenuClass(isPathActive(menu.link))}
              key={menu.value}
              onClick={() => switchRoute(menu.link, menu.query)}
            >
              {menu.label}
            </li>
          ))}

          {canShowDropdownMenus && (
            <>
              <li>
                <Button
                  aria-controls="manage-menu"
                  aria-haspopup="true"
                  onMouseEnter={onManageMouseEnter}
                  onMouseLeave={onManageMouseLeave}
                  className={getDesktopDropdownButtonClass(isManageActive)}
                  endIcon={
                    <ExpandMoreIcon
                      className={getDesktopDropdownIconClass(
                        Boolean(manageMenuAnchor)
                      )}
                    />
                  }
                >
                  Quản trị
                </Button>
              </li>
              <Popper
                id="manage-menu"
                open={Boolean(manageMenuAnchor)}
                anchorEl={manageMenuAnchor}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
                modifiers={{
                  flip: { enabled: false },
                  preventOverflow: {
                    enabled: false
                  },
                  computeStyle: {
                    gpuAcceleration: false
                  }
                }}
              >
                <ClickAwayListener onClickAway={closeManageMenu}>
                  <Paper
                    className={classes.desktopDropdownPaper}
                    onMouseEnter={onManageMenuMouseEnter}
                    onMouseLeave={onManageMenuMouseLeave}
                  >
                    <MenuList disablePadding>
                      {renderManageMenuItems(manageMenuItems)}
                    </MenuList>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </>
          )}

          {canShowReportMenu && (
            <>
              <li>
                <Button
                  aria-controls="report-menu"
                  aria-haspopup="true"
                  onMouseEnter={onReportMouseEnter}
                  onMouseLeave={onReportMouseLeave}
                  className={getDesktopDropdownButtonClass(isReportActive)}
                  endIcon={
                    <ExpandMoreIcon
                      className={getDesktopDropdownIconClass(
                        Boolean(reportMenuAnchor)
                      )}
                    />
                  }
                >
                  Báo cáo
                </Button>
              </li>
              {isReportMenuOpen && reportMenuStyle && (
                <ClickAwayListener
                  onClickAway={() => {
                    closeReportSubmenu();
                    closeReportMenu();
                  }}
                >
                  <Paper
                    ref={reportMenuPaperRef}
                    className={classes.desktopDropdownPaper}
                    style={reportMenuStyle}
                    onMouseEnter={() => {
                      clearReportSubmenuCloseTimeout();
                      onReportMenuMouseEnter();
                    }}
                    onMouseLeave={() => {
                      scheduleReportSubmenuClose();
                      onReportMenuMouseLeave();
                    }}
                  >
                    <MenuList disablePadding>
                      {renderReportMenuItems(reportMenuItems)}
                    </MenuList>
                  </Paper>
                </ClickAwayListener>
              )}
              {isReportMenuOpen &&
                Boolean(reportSubmenuAnchor) &&
                activeReportSubmenuItems.length > 0 &&
                reportSubmenuStyle && (
                <Paper
                  ref={reportSubmenuPaperRef}
                  className={classes.desktopSubmenuPaper}
                  style={reportSubmenuStyle}
                  onMouseEnter={() => {
                    clearReportSubmenuCloseTimeout();
                    onReportMenuMouseEnter();
                  }}
                  onMouseLeave={() => {
                    scheduleReportSubmenuClose();
                    onReportMenuMouseLeave();
                  }}
                >
                  {activeReportSubmenuItems.map((item, index) => (
                    <React.Fragment key={`${item.route}-${index}`}>
                      <MenuItem
                        onClick={() =>
                          !item.children?.length &&
                          handleReportSubmenuLeafItemClick(item.route)
                        }
                        className={classes.itemDropdownMenu}
                        onMouseEnter={event =>
                          handleReportSubmenuItemMouseEnter(event, item, index)
                        }
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <span>{item.label}</span>
                          {item.children?.length ? (
                            <ChevronRightIcon fontSize="small" />
                          ) : null}
                        </Box>
                      </MenuItem>
                      {index < activeReportSubmenuItems.length - 1 && (
                        <Divider className={classes.dropdownDivider} />
                      )}
                    </React.Fragment>
                  ))}
                </Paper>
              )}
              {isReportMenuOpen &&
                Boolean(reportNestedSubmenuAnchor) &&
                activeReportNestedSubmenuItems.length > 0 &&
                reportNestedSubmenuStyle && (
                <Paper
                  className={classes.desktopSubmenuPaper}
                  style={reportNestedSubmenuStyle}
                  onMouseEnter={() => {
                    clearReportSubmenuCloseTimeout();
                    onReportMenuMouseEnter();
                  }}
                  onMouseLeave={() => {
                    scheduleReportSubmenuClose();
                    onReportMenuMouseLeave();
                  }}
                >
                  {activeReportNestedSubmenuItems.map((item, index) => (
                    <React.Fragment key={`${item.route}-${index}`}>
                      <MenuItem
                        onClick={() => handleReportSubmenuLeafItemClick(item.route)}
                        className={classes.itemDropdownMenu}
                      >
                        {item.label}
                      </MenuItem>
                      {index < activeReportNestedSubmenuItems.length - 1 && (
                        <Divider className={classes.dropdownDivider} />
                      )}
                    </React.Fragment>
                  ))}
                </Paper>
              )}
            </>
          )}

          {canShowDeviceLookupMenu && (
            <li
              className={renderMainMenuClass(isDeviceLookupActive)}
              onClick={() => switchRoute('/manage/device-lookup')}
            >
              Tech Hub
            </li>
          )}
        </ul>
      </Box>
    </Hidden>
  );
};

TopBarDesktopNavigation.propTypes = {
  classes: PropTypes.object.isRequired,
  listMenu: PropTypes.array.isRequired,
  canShowDropdownMenus: PropTypes.bool.isRequired,
  canShowReportMenu: PropTypes.bool.isRequired,
  canShowDeviceLookupMenu: PropTypes.bool.isRequired,
  isPathActive: PropTypes.func.isRequired,
  isManageActive: PropTypes.bool.isRequired,
  isReportActive: PropTypes.bool.isRequired,
  isDeviceLookupActive: PropTypes.bool.isRequired,
  switchRoute: PropTypes.func.isRequired,
  manageMenuAnchor: PropTypes.any,
  reportMenuAnchor: PropTypes.any,
  onManageMouseEnter: PropTypes.func.isRequired,
  onManageMouseLeave: PropTypes.func.isRequired,
  onManageMenuMouseEnter: PropTypes.func.isRequired,
  onManageMenuMouseLeave: PropTypes.func.isRequired,
  onReportMouseEnter: PropTypes.func.isRequired,
  onReportMouseLeave: PropTypes.func.isRequired,
  onReportMenuMouseEnter: PropTypes.func.isRequired,
  onReportMenuMouseLeave: PropTypes.func.isRequired,
  closeManageMenu: PropTypes.func.isRequired,
  closeReportMenu: PropTypes.func.isRequired,
  manageMenuItems: PropTypes.array.isRequired,
  reportMenuItems: PropTypes.array.isRequired,
  handleDesktopMenuItemClick: PropTypes.func.isRequired
};

export default TopBarDesktopNavigation;
